import type { RunToolApprovalItem } from './items';
import logger from './logger';
import type { UnknownContext } from './types';
import { Usage } from './usage';

type ApprovalRecord = {
  approved: boolean | string[];
  rejected: boolean | string[];
  messages?: Record<string, string>;
  stickyRejectMessage?: string;
  updatedInput?: Record<string, unknown>;
};

const COMPUTER_APPROVAL_TOOL_NAMES = new Set([
  'computer',
  'computer_use_preview',
]);

function getApprovalToolNameCandidates(toolName: string): string[] {
  if (!COMPUTER_APPROVAL_TOOL_NAMES.has(toolName)) {
    return [toolName];
  }

  return toolName === 'computer'
    ? ['computer', 'computer_use_preview']
    : ['computer_use_preview', 'computer'];
}

type RunContextJson = {
  context: any;
  usage: Usage;
  approvals: Record<string, ApprovalRecord>;
  toolInput?: unknown;
};

/**
 * 一个上下文对象，会传递给 `Runner.run()` 方法。
 */

export class RunContext<TContext = UnknownContext> {
  /**
   * 传递给 `Runner.run()` 方法的上下文对象。
   */
  context: TContext;

  /**
   * 智能体运行截至目前的用量统计。对于流式响应，用量会实时更新。
   */
  usage: Usage;

  /**
   * 当前智能体工具运行时的结构化输入（如果可用）。
   */
  toolInput?: unknown;

  /**
   * 工具名称到其审批状态的映射表。
   */
  #approvals: Map<string, ApprovalRecord>;
  #ephemeralSharedState: Map<symbol, unknown>;

  constructor(context: TContext = {} as TContext) {
    this.context = context;
    this.usage = new Usage();
    this.#approvals = new Map();
    this.#ephemeralSharedState = new Map();
  }

  /**
   * 为分支运行创建子上下文实例。
   * 子类应重写此方法以安全地保留自定义实例状态。
   * @internal
   */
  protected _createFork(): RunContext<TContext> {
    return new RunContext(this.context);
  }

  /**
   * 将共享的运行时状态复制到子上下文中。
   * @internal
   */
  protected _cloneSharedState<TTarget extends RunContext<TContext>>(
    target: TTarget,
  ): TTarget {
    target.context = this.context;
    target.usage = this.usage;
    target.#approvals = this.#approvals;
    target.#ephemeralSharedState = this.#ephemeralSharedState;
    return target;
  }

  /**
   * 获取当前 run 生命周期内共享的临时状态。
   *
   * 该状态会在 forked tool contexts 之间共享，但不会序列化到 run state。
   *
   * @internal
   */
  _getEphemeralSharedState<T>(key: symbol, create: () => T): T {
    if (!this.#ephemeralSharedState.has(key)) {
      this.#ephemeralSharedState.set(key, create());
    }
    return this.#ephemeralSharedState.get(key) as T;
  }

  /**
   * 从序列化状态重建审批映射表。
   * @internal
   *
   * @param approvals - 需要重建的审批映射表。
   */
  _rebuildApprovals(approvals: Record<string, ApprovalRecord>) {
    this.#approvals = new Map();
    for (const [toolName, approval] of Object.entries(approvals)) {
      this.#setApprovalRecord(toolName, approval);
    }
  }

  /**
   * 合并来自序列化状态的审批记录，不丢弃已有条目。
   * @internal
   *
   * @param approvals - 需要合并的审批映射表。
   */
  _mergeApprovals(approvals: Record<string, ApprovalRecord>) {
    for (const [toolName, incoming] of Object.entries(approvals)) {
      this.#setApprovalRecord(toolName, incoming);
    }
  }

  /**
   * 获取调用者为特定工具调用提供的拒绝消息。
   *
   * @param toolName - 工具名称。
   * @param callId - 工具调用的调用 ID。
   * @returns 如果有提供消息则返回字符串，否则返回 `undefined`。
   */
  getRejectionMessage(toolName: string, callId: string): string | undefined {
    for (const approvalEntry of this.#getApprovalEntries(toolName)) {
      if (typeof approvalEntry.messages?.[callId] === 'string') {
        return approvalEntry.messages[callId];
      }
    }

    for (const approvalEntry of this.#getApprovalEntries(toolName)) {
      if (approvalEntry.rejected === true) {
        return approvalEntry.stickyRejectMessage;
      }
    }

    return undefined;
  }

  getApprovalUpdatedInput(toolName: string, callId: string): unknown {
    for (const approvalEntry of this.#getApprovalEntries(toolName)) {
      if (
        approvalEntry.updatedInput &&
        Object.hasOwn(approvalEntry.updatedInput, callId)
      ) {
        return approvalEntry.updatedInput[callId];
      }
    }

    return undefined;
  }

  #getCallId(approvalItem: RunToolApprovalItem): string {
    if ('callId' in approvalItem.rawItem) {
      return approvalItem.rawItem.callId;
    }

    const providerData = approvalItem.rawItem.providerData as
      | { itemId?: string; id?: string }
      | undefined;
    return (
      approvalItem.rawItem.id ?? providerData?.itemId ?? providerData?.id ?? ''
    );
  }

  /**
   * 检查某个工具调用是否已获批准。
   *
   * @param approval - 正在评估的工具调用的详细信息。
   * @returns 如果工具调用已获批准则返回 `true`，如果被阻止则返回 `false`，如果尚未批准或拒绝则返回 `undefined`。
   */
  isToolApproved(approval: { toolName: string; callId: string }) {
    const { toolName, callId } = approval;
    const approvalEntries = this.#getApprovalEntries(toolName);
    const hasPermanentApproval = approvalEntries.some(
      (approvalEntry) => approvalEntry.approved === true,
    );
    const hasPermanentRejection = approvalEntries.some(
      (approvalEntry) => approvalEntry.rejected === true,
    );

    if (hasPermanentApproval && hasPermanentRejection) {
      logger.warn('工具同时被永久批准和永久拒绝。批准优先。');
      return true;
    }

    if (hasPermanentApproval) {
      return true;
    }

    if (hasPermanentRejection) {
      return false;
    }

    const individualCallApproval = approvalEntries.some((approvalEntry) =>
      Array.isArray(approvalEntry.approved)
        ? approvalEntry.approved.includes(callId)
        : false,
    );
    const individualCallRejection = approvalEntries.some((approvalEntry) =>
      Array.isArray(approvalEntry.rejected)
        ? approvalEntry.rejected.includes(callId)
        : false,
    );

    if (individualCallApproval && individualCallRejection) {
      logger.warn(`工具调用 ${callId} 同时被批准和拒绝。批准优先。`);
      return true;
    }

    if (individualCallApproval) {
      return true;
    }

    if (individualCallRejection) {
      return false;
    }

    return undefined;
  }

  /**
   * 批准一个工具调用。
   *
   * @param approvalItem - 需要批准的工具审批项。
   * @param options - 额外的批准行为选项。
   */
  approveTool(
    approvalItem: RunToolApprovalItem,
    {
      alwaysApprove = false,
      updatedInput,
    }: { alwaysApprove?: boolean; updatedInput?: unknown } = {},
  ) {
    const toolName =
      approvalItem.toolName ?? (approvalItem.rawItem as any).name;
    const approvalKey = this.#getApprovalStorageKey(toolName);
    const callId = this.#getCallId(approvalItem);
    if (alwaysApprove) {
      this.#approvals.set(approvalKey, {
        approved: true,
        rejected: [],
        ...(updatedInput !== undefined
          ? { updatedInput: { [callId]: updatedInput } }
          : {}),
      });
      return;
    }

    const approvalEntry = this.#approvals.get(approvalKey) ?? {
      approved: [],
      rejected: [],
    };
    if (Array.isArray(approvalEntry.approved)) {
      approvalEntry.approved.push(callId);
    }
    if (updatedInput !== undefined) {
      approvalEntry.updatedInput = approvalEntry.updatedInput ?? {};
      approvalEntry.updatedInput[callId] = updatedInput;
    }
    this.#approvals.set(approvalKey, approvalEntry);
  }

  /**
   * 拒绝一个工具调用。
   *
   * @param approvalItem - 需要拒绝的工具审批项。
   */
  rejectTool(
    approvalItem: RunToolApprovalItem,
    {
      alwaysReject = false,
      message,
    }: { alwaysReject?: boolean; message?: string } = {},
  ) {
    const toolName =
      approvalItem.toolName ?? (approvalItem.rawItem as any).name;
    const approvalKey = this.#getApprovalStorageKey(toolName);
    if (alwaysReject) {
      const callId = this.#getCallId(approvalItem);
      this.#approvals.set(approvalKey, {
        approved: false,
        rejected: true,
        ...(message !== undefined
          ? {
              messages: { [callId]: message },
              stickyRejectMessage: message,
            }
          : {}),
      });
      return;
    }

    const approvalEntry = this.#approvals.get(approvalKey) ?? {
      approved: [] as string[],
      rejected: [] as string[],
    };

    if (Array.isArray(approvalEntry.rejected)) {
      const callId = this.#getCallId(approvalItem);
      approvalEntry.rejected.push(callId);
      if (message !== undefined) {
        approvalEntry.messages = approvalEntry.messages ?? {};
        approvalEntry.messages[callId] = message;
      }
    }
    this.#approvals.set(approvalKey, approvalEntry);
  }

  /**
   * 创建一个子上下文，共享审批状态和用量统计，并设置工具输入。
   * @internal
   */
  _forkWithToolInput(toolInput: unknown): RunContext<TContext> {
    const fork = this._cloneSharedState(this._createFork());
    fork.toolInput = toolInput;
    return fork;
  }

  /**
   * 创建一个子上下文，共享审批状态和用量统计，但不设置工具输入。
   * @internal
   */
  _forkWithoutToolInput(): RunContext<TContext> {
    const fork = this._cloneSharedState(this._createFork());
    fork.toolInput = undefined;
    return fork;
  }

  toJSON(): RunContextJson {
    const json: RunContextJson = {
      context: this.context,
      usage: this.usage,
      approvals: Object.fromEntries(this.#approvals.entries()),
    };
    if (typeof this.toolInput !== 'undefined') {
      json.toolInput = this.toolInput;
    }
    return json;
  }

  #getApprovalEntries(toolName: string): ApprovalRecord[] {
    return getApprovalToolNameCandidates(toolName)
      .map((candidate) => this.#approvals.get(candidate))
      .filter((approval): approval is ApprovalRecord => approval !== undefined);
  }

  #getApprovalStorageKey(toolName: string): string {
    return (
      getApprovalToolNameCandidates(toolName).find((candidate) =>
        this.#approvals.has(candidate),
      ) ?? toolName
    );
  }

  #setApprovalRecord(toolName: string, incoming: ApprovalRecord): void {
    const candidates = getApprovalToolNameCandidates(toolName);
    const existingEntries = candidates
      .map((candidate) => this.#approvals.get(candidate))
      .filter((approval): approval is ApprovalRecord => approval !== undefined);
    const mergedExisting = existingEntries.reduce<ApprovalRecord | undefined>(
      (current, approval) =>
        current ? mergeApprovalRecords(current, approval) : approval,
      undefined,
    );
    const merged = mergedExisting
      ? mergeApprovalRecords(mergedExisting, incoming)
      : incoming;
    const targetKey =
      candidates.find((candidate) => this.#approvals.has(candidate)) ??
      toolName;

    this.#approvals.set(targetKey, merged);
    for (const candidate of candidates) {
      if (candidate !== targetKey) {
        this.#approvals.delete(candidate);
      }
    }
  }
}

function mergeApprovalRecords(
  current: ApprovalRecord,
  incoming: ApprovalRecord,
): ApprovalRecord {
  const mergedMessages = mergeApprovalMessages(current, incoming);
  const stickyRejectMessage = mergeStickyRejectMessage(current, incoming);
  const updatedInput = mergeUpdatedInput(current, incoming);
  return {
    approved: mergeApprovalList(current.approved, incoming.approved),
    rejected: mergeApprovalList(current.rejected, incoming.rejected),
    ...(mergedMessages ? { messages: mergedMessages } : {}),
    ...(stickyRejectMessage !== undefined ? { stickyRejectMessage } : {}),
    ...(updatedInput ? { updatedInput } : {}),
  };
}

function mergeUpdatedInput(
  current: ApprovalRecord,
  incoming: ApprovalRecord,
): Record<string, unknown> | undefined {
  const merged =
    current.updatedInput || incoming.updatedInput
      ? { ...incoming.updatedInput, ...current.updatedInput }
      : undefined;
  return merged && Object.keys(merged).length > 0 ? merged : undefined;
}

function mergeApprovalMessages(
  current: ApprovalRecord,
  incoming: ApprovalRecord,
) {
  if (current.rejected === true) {
    return current.messages;
  }

  const merged =
    current.messages || incoming.messages
      ? { ...incoming.messages, ...current.messages }
      : undefined;
  if (!merged) {
    return undefined;
  }

  if (Array.isArray(current.rejected)) {
    for (const callId of current.rejected) {
      if (!current.messages || !Object.hasOwn(current.messages, callId)) {
        delete merged[callId];
      }
    }
  }

  return Object.keys(merged).length > 0 ? merged : undefined;
}

function mergeStickyRejectMessage(
  current: ApprovalRecord,
  incoming: ApprovalRecord,
) {
  if (current.rejected === true) {
    return current.stickyRejectMessage;
  }
  return current.stickyRejectMessage ?? incoming.stickyRejectMessage;
}

function mergeApprovalList(
  current: ApprovalRecord['approved'],
  incoming: ApprovalRecord['approved'],
) {
  if (current === true || incoming === true) {
    return true;
  }
  const currentList = Array.isArray(current) ? current : [];
  const incomingList = Array.isArray(incoming) ? incoming : [];
  return Array.from(new Set([...currentList, ...incomingList]));
}
